import { posCoord } from '../../shared/array-types';
import { createFormulaReadContext } from './formula-read-context';
import { createInventoryReadContext } from './inventory-read-context';
import { readInventoryField } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { buildPosition } from './inventory-initialization';
import { compileInventorySize } from './inventory-size';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { phpJsonTruthy } from './php-json-values';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseEncounterShellMetadata } from './from-base-encounter-shell';
import { inventoryPicture } from '../movement/inventory-support';

const DIFFICULT = '60160881-7F8B-A3ED-4D1A-D80C8397E634';
export interface EncounterTerrainMetadata extends Pick<FromBaseEncounterShellMetadata, 'layerCatalog' | 'abilityTypes' | 'definitionLookup'> {
    sizeCatalog: readonly Row[];
    navigationFootprints: { schema: string; items: Readonly<Record<string, Row>> };
}
export interface EncounterTerrainSource {
    entity_id: string;
    inventory: readonly Row[];
    missing_definition_ids?: readonly string[];
    /** Raw selected-pointer pictures; C1's attached galleries do not close these. */
    media_context?: readonly Row[];
    /** Native SELECT ordering, including name collation. Never derived from a prior blob. */
    floor_order?: readonly string[];
}

/** Finished floor rows include status/template changes. Raw standalone callers
 * retain named gaps until those owning producers complete. */
export function compileEncounterTerrainFromBase(base: EncounterTerrainSource, catalog: Readonly<Record<string, Row>>,
    metadata: EncounterTerrainMetadata, compiledFloor?: readonly Row[], onRowRefusal?: (id: string, reason: string) => void): Row {
    const definition = (id: string): Row | null => {
        if (compiledFloor && metadata.definitionLookup) return metadata.definitionLookup(id);
        const row = catalog[id] ?? catalog[id.toUpperCase()];
        if (row) return row;
        if (base.missing_definition_ids?.some(miss => miss.toUpperCase() === id.toUpperCase())) return null;
        throw new FromBaseInputMissing('definition', id);
    };
    const unavailable = (stage: string, detail: string): never => { throw new FromBaseStageMissing(stage, detail); };
    const inventoryContext = createInventoryReadContext(id => unavailable('terrain-inventory-context', id));
    const readers = createFormulaReadContext({ inventoryContext, catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: id => unavailable('terrain-inventory-context', id) });
    const rects: Row[] = [], boxes: Row[] = [], footprints: Row[] = [];
    const floor = (compiledFloor ?? base.inventory).filter(node => String(readInventoryField(node, 'possess_id')).toUpperCase() === base.entity_id.toUpperCase()
        && Number(readInventoryField(node, 'inventory_deleted') ?? 0) !== 1);
    if (floor.length && !base.floor_order) unavailable('terrain-floor-order', base.entity_id);
    const byId = new Map(floor.map(node => [String(readInventoryField(node, 'inventory_id')).toUpperCase(), node]));
    if (base.floor_order && (base.floor_order.length !== byId.size || new Set(base.floor_order.map(id => id.toUpperCase())).size !== byId.size))
        throw new TypeError('Incomplete terrain floor order');
    for (const key of base.floor_order ?? []) {
        const node = byId.get(key.toUpperCase());
        if (!node) throw new TypeError(`Unknown terrain floor id: ${key}`);
        try {
        const iid = readInventoryField(node, 'inventory_id'), item = readInventoryField(node, 'inventory_item_id');
        if (!fromBaseUuid(iid) || !fromBaseUuid(item)) throw new TypeError('Invalid floor inventory identity');
        const def = definition(item);
        if (!def) unavailable('terrain-definition', item);
        // These can add/change terrain even when the authored host currently lacks it.
        if (!compiledFloor && (phpJsonTruthy(readInventoryField(node, 'enchantment_template_id'))
            || ['template_id', 'enchantment_template_id', 'enchantment_template_applied'].some(name => phpJsonTruthy(readers.layerValue(def, name)))
            || Object.values(fromBaseRecord(node.statuses) ? node.statuses : {}).some(rows => rows != null && Object.keys(rows).length)))
            unavailable('terrain-definition-finalization', iid);
        const terrain = inventoryContext.withoutInstanceBridge(node, () => readers.layerValue(node, 'terrain_type'));
        const difficult = (value: unknown): boolean => typeof value === 'string' && value.toUpperCase() === DIFFICULT;
        if (!difficult(terrain) && !difficult(readers.layerValue(def, 'terrain_type'))) continue;
        if (phpJsonTruthy(readers.layerValue(def, 'swarm_width_lanes'))) unavailable('terrain-swarm-frontage', iid);
        let picture: Row | null | undefined;
        if (compiledFloor) picture = inventoryPicture(node, { value: readers.layerValue, definition });
        else {
            // Raw galleries retain SELECT order before picture construction.
            const gallery = Array.isArray(def!.pictures) ? def!.pictures as Row[] : unavailable('terrain-picture-gallery', item);
            const own = Array.isArray(node.pictures) ? node.pictures as Row[] : unavailable('terrain-picture-gallery', iid);
            const pointer = readInventoryField(node, 'inventory_presentation_pic_id');
            const selected = (pictures: readonly Row[]) => pictures.find(pic => {
                const id = readItemField(pic, 'picture_id');
                return typeof id === 'string' && typeof pointer === 'string' && id.toLowerCase() === pointer.toLowerCase();
            });
            picture = own.find(pic => readItemField(pic, 'picture_attached_item_type') === 'inventory_picture');
            if (!picture && fromBaseUuid(pointer)) {
                picture = selected(own) ?? selected(gallery) ?? (base.media_context ? selected(base.media_context) : undefined);
                if (!picture) unavailable('terrain-selected-picture', pointer);
            }
            picture ??= gallery.find(pic => readItemField(pic, 'picture_attached_item_type') === 'item');
            if (!picture && Object.entries(metadata.layerCatalog).some(([name, rule]) => rule.default_provider
                && phpJsonTruthy(readers.layerValue(def, name)))) unavailable('terrain-provider-picture', item);
        }
        let row = node;
        if (!compiledFloor) {
            const host = { ...fromBaseClone(def!), ...fromBaseClone(node) };
            host.item_layer_types = { ...fromBaseClone(def!.item_layer_types as Row), ...fromBaseClone(node.item_layer_types as Row ?? {}) };
            host.inventory = fromBaseClone(node.inventory);
            row = compileInventorySize(buildPosition(host, host.inventory as Row), metadata.sizeCatalog, readers.layerValue).row as Row;
        }
        const override = readInventoryField(node, 'inventory_size');
        const size = fromBaseUuid(override) ? override : inventoryContext.withoutInstanceBridge(row, () => readers.layerValue(row, 'size'))
            ?? readers.layerValue(def, 'size');
        const entry = metadata.sizeCatalog.find(entry => readItemField(entry, 'item_id') === size);
        if (!entry) unavailable('terrain-size-catalog', String(size));
        let transform = readInventoryField(node, 'transform_3d');
        if (typeof transform === 'string') { try { transform = JSON.parse(transform); } catch { transform = null; } }
        const normalized = fromBaseRecord(transform) && (transform.schema ?? 'ft.inventory-transform-3d.v1') === 'ft.inventory-transform-3d.v1' ? transform : {};
        const rotation = isPhpNumeric(normalized.rotationDegrees) ? phpJsonFloat(normalized.rotationDegrees) % 360 : 0;
        const scale = isPhpNumeric(normalized.scale) ? Math.max(0.05, Math.min(24, phpJsonFloat(normalized.scale))) : 1;
        const radians = rotation * Math.PI / 180, cos = Math.cos(radians), sin = Math.sin(radians);
        const authored = metadata.navigationFootprints.items[item] ?? metadata.navigationFootprints.items[item.toUpperCase()];
        // Absolute scale includes the resize's coarse size-band change already.
        // Independent size-only edits still use their explicit inventory override.
        const templateSize = readers.layerValue(def, 'size');
        const templateEntry = metadata.sizeCatalog.find(entry => readItemField(entry, 'item_id') === templateSize);
        const width = normalized.scaleMode === 'absolute'
            ? phpJsonFloat(readItemField(templateEntry ?? entry!, 'base_size')) * scale
            : phpJsonFloat(readItemField(entry!, 'base_size')) * (authored ? 1 : scale);
        const pw = readItemField(picture ?? null, 'picture_width') ?? 100;
        const ph = readItemField(picture ?? null, 'picture_height') ?? 100;
        const height = (phpJsonFloat(pw) !== 0 ? phpJsonFloat(ph) / phpJsonFloat(pw) : 1) * width;
        const x = posCoord(row, 'x_pos') ?? 0, y = posCoord(row, 'y_pos') ?? 0, z = posCoord(row, 'z_pos') ?? 0;
        const rect = { left: x - width / 2, right: x + width / 2, bottom: z - height / 2, top: z + height / 2 };
        const box = { ...rect, near: y - width / 2, far: y + width / 2, inventory_id: iid };
        rects.push(rect); boxes.push(box);
        const polygons: number[][][] = [];
        for (const polygon of Array.isArray(authored?.polygons) ? authored.polygons : []) {
            if (!Array.isArray(polygon) || polygon.length < 3 || !polygon.every(point => Array.isArray(point)
                && isPhpNumeric(point[0]) && isPhpNumeric(point[1]))) continue;
            polygons.push(polygon.map(point => { const dx = phpJsonFloat(point[0]) * scale, dy = phpJsonFloat(point[1]) * scale;
                return [x + dx * cos - dy * sin, y + dx * sin + dy * cos]; }));
        }
        const source = polygons.length ? 'authored-item-footprint' : 'size-box-fallback';
        if (!polygons.length) polygons.push([[box.left, box.near], [box.right, box.near], [box.right, box.far], [box.left, box.far]]);
        footprints.push({ schema: 'ft.ft3d-navigation-footprint.v1', source, bottom: box.bottom, top: box.top,
            polygons, inventory_id: iid, item_id: item });
        } catch (error) {
            if (!onRowRefusal || !(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
            onRowRefusal(key, error.message);
        }
    }
    // Native thins empty indexes and preserves floor SELECT order in every index.
    return footprints.length ? { difficult_terrain_rects: rects, difficult_terrain_boxes: boxes,
        difficult_terrain_footprints: footprints, difficult_terrain_inventory_ids: [...new Set(footprints.map(row => row.inventory_id))] } : {};
}

const terrainKeys = ['difficult_terrain_rects', 'difficult_terrain_boxes', 'difficult_terrain_footprints', 'difficult_terrain_inventory_ids'] as const;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** Reuse initial compilation for ONE changed floor row; every other footprint stays held. */
export function patchEncounterTerrainRows(before: Row, after: Row, ids: ReadonlySet<string>, metadata: EncounterTerrainMetadata): Row {
    if (!ids.size) return after;
    const catalog = (fromBaseRecord(after.catalog) ? after.catalog : {}) as Record<string, Row>;
    const floor = Array.isArray(after.encounter_inventories_arr) ? after.encounter_inventories_arr.filter(fromBaseRecord) : [];
    let next = after;
    const arrays = new Map<string, unknown[]>();
    const writable = (key: typeof terrainKeys[number]): unknown[] => {
        let array = arrays.get(key);
        if (!array) { array = Array.isArray(before[key]) ? before[key].slice() : []; arrays.set(key, array); }
        return array;
    };
    for (const id of ids) {
        const row = floor.find(row => upper(readInventoryField(row, 'inventory_id')) === id);
        const oldRows = arrays.get('difficult_terrain_footprints') ?? (Array.isArray(before.difficult_terrain_footprints) ? before.difficult_terrain_footprints : []);
        const index = oldRows.findIndex(row => fromBaseRecord(row) && upper(row.inventory_id) === id);
        // First-compiled births carry their finalized item and carrier catalog.
        // Their item need not be in the encounter's original catalog (a new corpse).
        const local = fromBaseRecord(row?.catalog) ? row.catalog as Record<string, Row> : catalog;
        const item = upper(readItemField(row ?? null, 'item_id'));
        const terrain = row ? compileEncounterTerrainFromBase({ entity_id: String(readInventoryField(row, 'possess_id')),
            inventory: [row], floor_order: [id] }, catalog,
            { ...metadata, definitionLookup: key => metadata.definitionLookup?.(key) ?? catalog[upper(key)]
                ?? local[upper(key)] ?? (upper(key) === item ? row : null) }, [row]) : {};
        const present = Array.isArray(terrain.difficult_terrain_footprints) && terrain.difficult_terrain_footprints.length > 0;
        if (index < 0 && !present) continue;
        if (index >= 0 && present && terrainKeys.every(key => {
            const prior = arrays.get(key) ?? (Array.isArray(before[key]) ? before[key] : []);
            return JSON.stringify(prior[index]) === JSON.stringify((terrain[key] as unknown[])[0]);
        })) continue;
        for (const key of terrainKeys) {
            const list = writable(key);
            const incoming = Array.isArray(terrain[key]) ? terrain[key][0] : undefined;
            if (index < 0) list.push(incoming);
            else if (present) list[index] = incoming;
            else list.splice(index, 1);
        }
    }
    if (arrays.size) {
        next = { ...after };
        for (const [key, value] of arrays) next[key] = value;
    }
    return next;
}
